import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { callEdgeFunction, supabase } from '../lib/supabase';

export function BulkUploadPage({ profile, allProfiles, units, trainingPlans, knowledgeUnits, signoffs, tradeSchoolLogs, onUpsertPlan, onToggleKnowledge, onToggleSignoff, onAddTS, onReloadSignoffs, onReloadPlans }) {
  const [tab, setTab] = useState('smartpdf');
  const [csvData, setCsvData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  // ── Smart PDF state ──
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfParseType, setPdfParseType] = useState('training_plan');
  const [pdfResult, setPdfResult] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfImportResult, setPdfImportResult] = useState(null);
  const pdfRef = useRef();

  const apprentices = allProfiles.filter(p => p.role === 'apprentice');
  const apprenticeMap = {};
  apprentices.forEach(a => {
    apprenticeMap[a.name?.toLowerCase().trim()] = a;
    if (a.email) apprenticeMap[a.email.toLowerCase().trim()] = a;
    if (a.student_number) apprenticeMap[String(a.student_number).trim()] = a;
  });

  const unitMap = {};
  (units || []).forEach(u => {
    unitMap[u.code?.toLowerCase().trim()] = u;
  });

  function reset() {
    setCsvData(null);
    setPreview(null);
    setResult(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  }

  function parseCSV(text) {
    const result = Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim().replace(/^["']|["']$/g, '').toLowerCase(),
    });
    if (!result.data || result.data.length === 0) return null;
    return { headers: result.meta.fields, rows: result.data };
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setResult(null);
    setPreview(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const parsed = parseCSV(text);
      if (!parsed || parsed.rows.length === 0) {
        setError('Could not parse CSV — make sure it has headers and data rows.');
        return;
      }
      setCsvData(parsed);

      if (tab === 'enrolments') {
        validateEnrolments(parsed);
      } else {
        validateResults(parsed);
      }
    };
    reader.readAsText(file);
  }

  function validateEnrolments(parsed) {
    const nameCol = parsed.headers.find(h => h.includes('apprentice') || h.includes('name') || h.includes('student'));
    const unitCol = parsed.headers.find(h => h.includes('unit') && h.includes('code') || h === 'unit_code' || h === 'code');
    const yearCol = parsed.headers.find(h => h.includes('year') || h.includes('level'));

    if (!nameCol || !unitCol || !yearCol) {
      setError(`Missing required columns. Found: ${parsed.headers.join(', ')}. Need columns for: apprentice name, unit code, and year.`);
      return;
    }

    const items = parsed.rows.map((row, idx) => {
      const apprKey = row[nameCol]?.toLowerCase().trim();
      const unitKey = row[unitCol]?.toLowerCase().trim();
      const year = parseInt(row[yearCol]);
      const appr = apprenticeMap[apprKey];
      const unit = unitMap[unitKey];
      const errors = [];

      if (!appr) errors.push(`Apprentice "${row[nameCol]}" not found`);
      if (!unit) errors.push(`Unit "${row[unitCol]}" not found`);
      if (!year || year < 1 || year > 3) errors.push(`Invalid year "${row[yearCol]}"`);

      return {
        row: idx + 2,
        apprenticeName: row[nameCol],
        unitCode: row[unitCol]?.toUpperCase(),
        year,
        apprenticeId: appr?.id,
        valid: errors.length === 0,
        errors,
      };
    });

    setPreview({ type: 'enrolments', nameCol, unitCol, yearCol, items });
  }

  async function importEnrolments() {
    if (!preview || preview.type !== 'enrolments') return;
    setImporting(true);
    const valid = preview.items.filter(i => i.valid);
    let success = 0, failed = 0;

    for (const item of valid) {
      try {
        await onUpsertPlan(item.apprenticeId, item.unitCode, item.year);
        success++;
      } catch {
        failed++;
      }
    }

    setImporting(false);
    setResult({ total: preview.items.length, valid: valid.length, skipped: preview.items.length - valid.length, success, failed });
  }

  function validateResults(parsed) {
    const nameCol = parsed.headers.find(h => h.includes('apprentice') || h.includes('name') || h.includes('student'));
    const unitCol = parsed.headers.find(h => h.includes('unit') && h.includes('code') || h === 'unit_code' || h === 'code');
    const statusCol = parsed.headers.find(h => h.includes('status') || h.includes('result') || h.includes('passed') || h.includes('knowledge'));

    if (!nameCol || !unitCol) {
      setError(`Missing required columns. Found: ${parsed.headers.join(', ')}. Need columns for: apprentice name and unit code. Status/result column is optional (defaults to passed).`);
      return;
    }

    const items = parsed.rows.map((row, idx) => {
      const apprKey = row[nameCol]?.toLowerCase().trim();
      const unitKey = row[unitCol]?.toLowerCase().trim();
      const statusRaw = statusCol ? row[statusCol]?.toLowerCase().trim() : 'p';
      const appr = apprenticeMap[apprKey];
      const unit = unitMap[unitKey];
      const errors = [];

      const isPassed = ['p', 'passed', 'pass', 'yes', 'y', 'complete', 'completed', 'true', '1', 'c'].includes(statusRaw);
      const isExempt = ['ex', 'exempt', 'exemption', 'rpl'].includes(statusRaw);
      const isEnrolled = ['e', 'enrolled', 'training', 'outstanding', 'enrolled'].includes(statusRaw);

      if (!appr) errors.push(`Apprentice "${row[nameCol]}" not found`);
      if (!unit) errors.push(`Unit "${row[unitCol]}" not found`);
      if (!isPassed && !isExempt && !isEnrolled && statusCol) errors.push(`Unknown status "${row[statusCol]}"`);

      const alreadyPassed = knowledgeUnits.some(k => k.apprentice_id === appr?.id && k.unit_code === unitKey?.toUpperCase());

      return {
        row: idx + 2,
        apprenticeName: row[nameCol],
        unitCode: row[unitCol]?.toUpperCase(),
        status: isPassed ? 'passed' : isExempt ? 'exempt' : isEnrolled ? 'enrolled' : 'unknown',
        statusRaw: statusCol ? row[statusCol] : '—',
        apprenticeId: appr?.id,
        alreadyPassed,
        valid: errors.length === 0 && (isPassed || isExempt),
        skip: isEnrolled,
        errors,
      };
    });

    setPreview({ type: 'results', nameCol, unitCol, statusCol, items });
  }

  async function importResults() {
    if (!preview || preview.type !== 'results') return;
    setImporting(true);
    const valid = preview.items.filter(i => i.valid && !i.alreadyPassed);
    let success = 0, failed = 0;

    for (const item of valid) {
      try {
        await onToggleKnowledge(item.apprenticeId, item.unitCode, true, profile.id);
        success++;
      } catch {
        failed++;
      }
    }

    setImporting(false);
    const skippedEnrolled = preview.items.filter(i => i.skip).length;
    const alreadyDone = preview.items.filter(i => i.valid && i.alreadyPassed).length;
    setResult({ total: preview.items.length, success, failed, skippedEnrolled, alreadyDone, skippedErrors: preview.items.filter(i => !i.valid && !i.skip).length });
  }

  const validCount = preview?.items.filter(i => i.valid && !(i.alreadyPassed)).length || 0;
  const errorCount = preview?.items.filter(i => !i.valid && !i.skip).length || 0;

  // ── Smart PDF functions ──
  function resetPdf() {
    setPdfFile(null);
    setPdfResult(null);
    setPdfPreview(null);
    setPdfImportResult(null);
    setError('');
    if (pdfRef.current) pdfRef.current.value = '';
  }

  async function extractPdfText(file) {
    // Use pdf.js from CDN
    const pdfjsLib = await import('https://cdn.jsdelivr.net/npm/pdfjs-dist@4.0.379/+esm');
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs';

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items.map(item => item.str).join(' ');
      fullText += pageText + '\n\n';
    }

    return fullText;
  }

  async function handlePdfUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfFile(file);
    setError('');
    setPdfResult(null);
    setPdfPreview(null);
    setPdfImportResult(null);
  }

  async function parsePdf() {
    if (!pdfFile) return;
    setPdfParsing(true);
    setError('');

    try {
      // Step 1: Extract text from PDF
      const pdfText = await extractPdfText(pdfFile);

      if (!pdfText || pdfText.trim().length < 20) {
        setError('Could not extract text from this PDF. It might be scanned/image-based. Try a text-based PDF or use the CSV import instead.');
        setPdfParsing(false);
        return;
      }

      // Step 2: Send to edge function for AI parsing
      const unitCodes = (units || []).map(u => u.code);
      const apprenticeNames = allProfiles.filter(p => p.role === 'apprentice').map(p => p.name);

      const parsed = await callEdgeFunction('parse-training-pdf', {
        pdfText,
        parseType: pdfParseType,
        unitCodes,
        apprenticeNames,
      });
      setPdfResult(parsed);

      // Step 3: Build preview by matching against known data
      const items = (parsed.entries || []).map((entry, idx) => {
        const matchedUnit = (units || []).find(u => u.code === entry.unit_code?.toUpperCase());
        const apprName = parsed.apprentice_name || entry.apprentice_name || '';
        const matchedAppr = apprenticeMap[apprName.toLowerCase().trim()];
        const errors = [];

        if (!matchedUnit) errors.push(`Unit "${entry.unit_code}" not in system`);
        if (!matchedAppr) errors.push(`Apprentice "${apprName}" not matched`);

        if (pdfParseType === 'training_plan') {
          const year = entry.year || 0;
          if (year < 1 || year > 3) errors.push(`Year "${year}" invalid — needs manual assignment`);

          return {
            row: idx + 1,
            apprenticeName: apprName,
            unitCode: entry.unit_code?.toUpperCase(),
            unitName: entry.unit_name || matchedUnit?.name || '',
            year,
            status: entry.status,
            confidence: entry.confidence,
            notes: entry.notes,
            apprenticeId: matchedAppr?.id,
            valid: errors.length === 0 && year >= 1 && year <= 3,
            errors,
          };
        } else {
          const isPassed = ['passed', 'exempt'].includes(entry.status);
          const isKnowledgePassed = entry.status === 'knowledge_passed';
          const unitCode = entry.unit_code?.toUpperCase();
          const hasKnowledge = knowledgeUnits.some(k => k.apprentice_id === matchedAppr?.id && k.unit_code === unitCode);
          const hasEvidence = (signoffs || []).some(s => s.apprentice_id === matchedAppr?.id && s.unit_code === unitCode);
          const fullyDone = hasKnowledge && hasEvidence;
          const knowledgeOnly = hasKnowledge && !hasEvidence;

          return {
            row: idx + 1,
            apprenticeName: apprName,
            unitCode,
            unitName: entry.unit_name || matchedUnit?.name || '',
            status: entry.status,
            statusDisplay: isKnowledgePassed ? 'knowledge passed' : entry.status,
            date: entry.date,
            confidence: entry.confidence,
            notes: entry.notes,
            apprenticeId: matchedAppr?.id,
            hasKnowledge,
            hasEvidence,
            alreadyPassed: fullyDone,
            needsEvidenceOnly: knowledgeOnly && isPassed,
            valid: errors.length === 0 && (isPassed || isKnowledgePassed) && !fullyDone,
            skip: !isPassed && !isKnowledgePassed,
            errors,
          };
        }
      });

      if (items.length === 0) {
        setError("No units found in that PDF. Check it's a training plan or a progress/results document, then try again.");
        setPdfParsing(false);
        return;
      }

      setPdfPreview({
        type: pdfParseType,
        apprenticeName: parsed.apprentice_name,
        documentType: parsed.document_type,
        warnings: parsed.warnings || [],
        items,
      });

    } catch (err) {
      console.error('PDF parse error:', err);
      // Server messages are short fixed strings from parse-training-pdf; cap length anyway.
      const detail = String(err?.message || '').replace(/\s+/g, ' ').trim().slice(0, 200);
      setError(`Couldn't read that PDF – try again, or contact support.${detail ? ` (${detail})` : ''}`);
    }

    setPdfParsing(false);
  }

  async function importPdfEnrolments() {
    if (!pdfPreview) return;
    setPdfImporting(true);
    const valid = pdfPreview.items.filter(i => i.valid);
    let success = 0, failed = 0;

    for (const item of valid) {
      try {
        await onUpsertPlan(item.apprenticeId, item.unitCode, item.year);
        success++;
      } catch {
        failed++;
      }
    }

    // Save commencement date to apprentice profile if extracted
    let dateSaved = false;
    if (pdfResult?.commencement_date && valid.length > 0) {
      const apprenticeId = valid[0].apprenticeId;
      try {
        await supabase.from('profiles').update({ start_date: pdfResult.commencement_date }).eq('id', apprenticeId);
        dateSaved = true;
      } catch (e) {
        console.error('Failed to save commencement date:', e);
      }
    }

    setPdfImporting(false);
    setPdfImportResult({
      total: pdfPreview.items.length,
      valid: valid.length,
      skipped: pdfPreview.items.length - valid.length,
      success,
      failed,
      dateSaved,
    });
  }

  async function importPdfResults() {
    if (!pdfPreview) return;
    setPdfImporting(true);
    const valid = pdfPreview.items.filter(i => i.valid);
    let success = 0, failed = 0;

    for (const item of valid) {
      try {
        // Only mark knowledge if not already done
        if (!item.hasKnowledge) {
          await onToggleKnowledge(item.apprenticeId, item.unitCode, true, profile.id);
        }
        // If fully passed (not just knowledge), also mark evidence signoff if not already done
        if ((item.status === 'passed' || item.status === 'exempt') && !item.hasEvidence) {
          await onToggleSignoff(item.apprenticeId, item.unitCode, true, profile.id);
        }
        success++;
      } catch {
        failed++;
      }
    }

    setPdfImporting(false);
    const alreadyDone = pdfPreview.items.filter(i => i.alreadyPassed).length;
    const skippedNotPassed = pdfPreview.items.filter(i => i.skip).length;
    setPdfImportResult({ total: pdfPreview.items.length, success, failed, alreadyDone, skippedNotPassed });
  }

  const pdfValidCount = pdfPreview?.items.filter(i => i.valid && !i.alreadyPassed).length || 0;
  const pdfErrorCount = pdfPreview?.items.filter(i => !i.valid && !i.skip).length || 0;

  // ── Trade School CSV functions ──
  const [tsPreview, setTsPreview] = useState(null);
  const [tsImporting, setTsImporting] = useState(false);
  const [tsResult, setTsResult] = useState(null);
  const tsFileRef = useRef();

  function resetTs() {
    setTsPreview(null);
    setTsResult(null);
    setError('');
    if (tsFileRef.current) tsFileRef.current.value = '';
  }

  function handleTsFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setTsResult(null);
    setTsPreview(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const parsed = parseCSV(text);
      if (!parsed || parsed.rows.length === 0) {
        setError('Could not parse CSV — make sure it has headers and data rows.');
        return;
      }
      validateTradeSchool(parsed);
    };
    reader.readAsText(file);
  }

  function validateTradeSchool(parsed) {
    const nameCol = parsed.headers.find(h => h.includes('apprentice') || h.includes('name') || h.includes('student') || h.includes('employee'));
    const dateCol = parsed.headers.find(h => h.includes('date'));
    const hoursCol = parsed.headers.find(h => h.includes('hour'));
    const topicCol = parsed.headers.find(h => h.includes('topic') || h.includes('subject') || h.includes('description') || h.includes('notes') || h.includes('comment'));

    if (!nameCol || !dateCol || !hoursCol) {
      setError(`Missing required columns. Found: ${parsed.headers.join(', ')}. Need columns for: apprentice/name, date, and hours. Topic column is optional.`);
      return;
    }

    const items = parsed.rows.map((row, idx) => {
      const apprKey = row[nameCol]?.toLowerCase().trim();
      const dateRaw = row[dateCol]?.trim();
      const hoursRaw = row[hoursCol]?.trim();
      const topic = topicCol ? row[topicCol]?.trim() : 'Trade school attendance';
      const appr = apprenticeMap[apprKey];
      const errors = [];

      if (!appr) errors.push(`"${row[nameCol]}" not found`);

      // Parse date - handle DD/MM/YYYY, YYYY-MM-DD, etc
      let parsedDate = null;
      if (dateRaw) {
        if (dateRaw.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
          const parts = dateRaw.split('/');
          parsedDate = `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
        } else if (dateRaw.match(/^\d{4}-\d{2}-\d{2}/)) {
          parsedDate = dateRaw.slice(0, 10);
        } else {
          const d = new Date(dateRaw);
          if (!isNaN(d)) parsedDate = d.toISOString().slice(0, 10);
        }
      }
      if (!parsedDate) errors.push(`Invalid date "${dateRaw}"`);

      const hours = parseFloat(hoursRaw);
      if (isNaN(hours) || hours <= 0) errors.push(`Invalid hours "${hoursRaw}"`);

      // Check for duplicates
      const isDuplicate = (tradeSchoolLogs || []).some(l =>
        l.apprentice_id === appr?.id && l.log_date === parsedDate && Math.abs(Number(l.hours) - hours) < 0.01
      );

      return {
        row: idx + 2,
        apprenticeName: row[nameCol],
        date: parsedDate,
        dateRaw,
        hours,
        topic: topic || 'Trade school attendance',
        apprenticeId: appr?.id,
        isDuplicate,
        valid: errors.length === 0 && !isDuplicate,
        errors,
      };
    });

    setTsPreview({ items, nameCol, dateCol, hoursCol, topicCol });
  }

  async function importTradeSchool() {
    if (!tsPreview) return;
    setTsImporting(true);
    const valid = tsPreview.items.filter(i => i.valid);
    let success = 0, failed = 0;

    for (const item of valid) {
      try {
        await onAddTS({ apprentice_id: item.apprenticeId, log_date: item.date, hours: item.hours, topic: item.topic });
        success++;
      } catch {
        failed++;
      }
    }

    setTsImporting(false);
    const duplicates = tsPreview.items.filter(i => i.isDuplicate).length;
    const errored = tsPreview.items.filter(i => !i.valid && !i.isDuplicate).length;
    setTsResult({ total: tsPreview.items.length, success, failed, duplicates, errored });
  }

  const tsValidCount = tsPreview?.items.filter(i => i.valid).length || 0;
  const tsErrorCount = tsPreview?.items.filter(i => !i.valid && !i.isDuplicate).length || 0;
  const tsDupeCount = tsPreview?.items.filter(i => i.isDuplicate).length || 0;
  const tsTotalHours = Math.round((tsPreview?.items.filter(i => i.valid).reduce((a, i) => a + (i.hours || 0), 0) || 0) * 100) / 100;

  return (
    <div>
      <div className="page-hdr">
        <div>
          <div className="page-title">Bulk Upload</div>
          <div className="page-sub">Import from PDF or CSV</div>
        </div>
      </div>

      <div className="tab-group">
        <button className={`btn ${tab === 'smartpdf' ? '' : 'outline'}`} onClick={() => { setTab('smartpdf'); reset(); }}>
          📄 Smart PDF Import
        </button>
        <button className={`btn ${tab === 'enrolments' ? '' : 'outline'}`} onClick={() => { setTab('enrolments'); reset(); resetPdf(); }}>
          📐 CSV Enrolments
        </button>
        <button className={`btn ${tab === 'results' ? '' : 'outline'}`} onClick={() => { setTab('results'); reset(); resetPdf(); }}>
          🎓 CSV Results
        </button>
        <button className={`btn ${tab === 'tradeschool' ? '' : 'outline'}`} onClick={() => { setTab('tradeschool'); reset(); resetPdf(); }}>
          🏫 CSV Trade School
        </button>
      </div>

      {/* ═══ Smart PDF Import ═══ */}
      {tab === 'smartpdf' && (
        <>
          <div className="card mb-16">
            <div className="card-hdr">
              <span className="card-title">📄 Smart PDF Import</span>
            </div>
            <div className="card-body">
              <p className="fs-13 mb-12" style={{ lineHeight: 1.7 }}>
                Drop in a training plan or progress report PDF from your RTO and we'll extract the data automatically. No need to create a spreadsheet.
              </p>

              <div className="infobox mb-16">
                <strong>What PDFs work?</strong><br />
                Training plans, enrolment summaries, progress reports, and results statements from RTOs like Bendigo Kangan Institute.
                The PDF needs to be text-based (not a scanned image).
              </div>

              {/* Parse type selector */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--head)', display: 'block', marginBottom: 6 }}>
                  What type of document is this?
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    style={{
                      padding: '8px 16px', fontSize: 13, fontFamily: 'var(--font-d)', fontWeight: 700,
                      letterSpacing: 0.5, textTransform: 'uppercase', border: '2px solid',
                      borderRadius: 4, cursor: 'pointer', transition: 'all .15s',
                      background: pdfParseType === 'training_plan' ? 'var(--accent)' : 'transparent',
                      color: pdfParseType === 'training_plan' ? '#000' : 'var(--muted)',
                      borderColor: pdfParseType === 'training_plan' ? 'var(--accent)' : 'var(--border)',
                    }}
                    onClick={() => setPdfParseType('training_plan')}
                  >
                    📐 Training Plan / Enrolment
                  </button>
                  <button
                    style={{
                      padding: '8px 16px', fontSize: 13, fontFamily: 'var(--font-d)', fontWeight: 700,
                      letterSpacing: 0.5, textTransform: 'uppercase', border: '2px solid',
                      borderRadius: 4, cursor: 'pointer', transition: 'all .15s',
                      background: pdfParseType === 'progress_report' ? 'var(--accent)' : 'transparent',
                      color: pdfParseType === 'progress_report' ? '#000' : 'var(--muted)',
                      borderColor: pdfParseType === 'progress_report' ? 'var(--accent)' : 'var(--border)',
                    }}
                    onClick={() => setPdfParseType('progress_report')}
                  >
                    🎓 Progress Report / Results
                  </button>
                </div>
              </div>

              {/* File input */}
              <div className="flex items-center gap-12">
                <label
                  className="btn outline"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}
                >
                  📎 Choose PDF File
                  <input ref={pdfRef} type="file" accept=".pdf" onChange={handlePdfUpload} style={{ display: 'none' }} />
                </label>
                {pdfFile && !pdfParsing && !pdfPreview && (
                  <button className="btn" onClick={parsePdf}>
                    🤖 Parse PDF
                  </button>
                )}
                {(pdfPreview || pdfResult || error) && (
                  <button className="btn outline fs-12" onClick={resetPdf}>Clear</button>
                )}
              </div>

              {pdfFile && !pdfParsing && !pdfPreview && !pdfImportResult && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
                  📎 {pdfFile.name} ({(pdfFile.size / 1024).toFixed(0)} KB) — click "Parse PDF" to extract data
                </div>
              )}

              {pdfParsing && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div className="spin" />
                    <span className="fs-13">Extracting and parsing PDF… this may take a few seconds</span>
                  </div>
                  <div style={{ padding: '8px 12px', borderRadius: 6, background: '#fef3c7', border: '1px solid #fde68a', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                    ⚠️ Do not refresh or close this screen while parsing is in progress
                  </div>
                </div>
              )}
            </div>
          </div>

          {error && <div className="error-box">{error}</div>}

          {/* Warnings from AI */}
          {pdfPreview?.warnings?.length > 0 && (
            <div className="card mb-16" style={{ borderLeft: '3px solid #f59e0b' }}>
              <div className="card-body">
                <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>⚠️ Parser Notes</div>
                {pdfPreview.warnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>• {w}</div>
                ))}
              </div>
            </div>
          )}

          {/* Commencement date from training plan */}
          {pdfPreview && pdfResult?.commencement_date && pdfPreview.type === 'training_plan' && !pdfImportResult && (
            <div className="card mb-16" style={{ borderLeft: '3px solid var(--accent2)' }}>
              <div className="card-body">
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--head)', marginBottom: 4 }}>📅 Commencement Date Found</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-d)' }}>
                  {pdfResult.commencement_date}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>This will be saved as the apprentice's start date on import</div>
              </div>
            </div>
          )}

          {/* PDF Preview table */}
          {pdfPreview && !pdfImportResult && (
            <div className="card mb-16">
              <div className="card-hdr">
                <span className="card-title">
                  Preview — {pdfPreview.items.length} units found
                  {pdfPreview.apprenticeName && ` for ${pdfPreview.apprenticeName}`}
                </span>
                <div className="flex gap-8">
                  {pdfValidCount > 0 && <span className="pill pill-green">{pdfValidCount} ready</span>}
                  {pdfErrorCount > 0 && <span className="pill pill-red">{pdfErrorCount} issues</span>}
                </div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Unit Code</th>
                      <th>Unit Name</th>
                      {pdfPreview.type === 'training_plan' ? <th>Year</th> : <th>Status</th>}
                      <th>Confidence</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pdfPreview.items.map((item, i) => (
                      <tr key={i} style={item.skip || item.alreadyPassed ? { opacity: 0.5 } : undefined}>
                        <td className="fs-11 text-muted">{item.row}</td>
                        <td style={{ fontFamily: 'var(--font-d)', fontWeight: 700, color: 'var(--accent)', letterSpacing: 0.5, fontSize: 13 }}>
                          {item.unitCode}
                        </td>
                        <td style={{ fontSize: 12 }}>{item.unitName}</td>
                        {pdfPreview.type === 'training_plan' ? (
                          <td>{item.year > 0 ? `Year ${item.year}` : <span className="text-red fs-11">?</span>}</td>
                        ) : (
                          <td>
                            <span style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                              padding: '2px 7px', borderRadius: 4,
                              background: item.status === 'passed' ? '#dcfce7' : item.status === 'exempt' ? '#dbeafe' : item.status === 'knowledge_passed' ? '#fef3c7' : '#f3f4f6',
                              color: item.status === 'passed' ? '#166534' : item.status === 'exempt' ? '#1e40af' : item.status === 'knowledge_passed' ? '#92400e' : '#6b7280',
                            }}>
                              {item.status === 'knowledge_passed' ? '📚 knowledge' : item.status}
                            </span>
                          </td>
                        )}
                        <td>
                          <span style={{
                            fontSize: 10, fontWeight: 600,
                            color: item.confidence === 'high' ? 'var(--green)' : item.confidence === 'medium' ? '#d97706' : '#ef4444',
                          }}>
                            {item.confidence === 'high' ? '●' : item.confidence === 'medium' ? '●' : '●'} {item.confidence}
                          </span>
                        </td>
                        <td>
                          {item.valid && !item.hasKnowledge && !item.hasEvidence && <span className="fs-11 text-green">✓ Ready</span>}
                          {item.valid && item.needsEvidenceOnly && <span className="fs-11 text-green">✓ Evidence needed</span>}
                          {item.alreadyPassed && <span className="fs-11 text-muted">Already done</span>}
                          {item.skip && <span className="fs-11 text-muted">Skip ({item.status})</span>}
                          {!item.valid && !item.skip && !item.alreadyPassed && <span className="fs-11 text-red">{item.errors.join('; ')}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-footer">
                <button
                  className="btn"
                  onClick={pdfPreview.type === 'training_plan' ? importPdfEnrolments : importPdfResults}
                  disabled={pdfImporting || pdfValidCount === 0}
                >
                  {pdfImporting ? 'Importing…' : `Import ${pdfValidCount} ${pdfPreview.type === 'training_plan' ? 'enrolments' : 'results'}`}
                </button>
                {pdfValidCount === 0 && <span className="fs-12 text-muted">No valid rows to import — check the issues above</span>}
              </div>
            </div>
          )}

          {/* PDF Import result */}
          {pdfImportResult && (
            <div className="card">
              <div className="card-hdr">
                <span className="card-title">Import Complete</span>
              </div>
              <div className="card-body">
                <div className="flex flex-wrap gap-12 mb-16">
                  <div className="result-stat success">
                    <div className="result-stat-value">{pdfImportResult.success}</div>
                    <div className="result-stat-label">Imported</div>
                  </div>
                  {pdfImportResult.failed > 0 && (
                    <div className="result-stat error">
                      <div className="result-stat-value">{pdfImportResult.failed}</div>
                      <div className="result-stat-label">Failed</div>
                    </div>
                  )}
                  {(pdfImportResult.alreadyDone || 0) > 0 && (
                    <div className="result-stat neutral">
                      <div className="result-stat-value">{pdfImportResult.alreadyDone}</div>
                      <div className="result-stat-label">Already done</div>
                    </div>
                  )}
                </div>
                <button className="btn outline" onClick={resetPdf}>Upload another PDF</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ CSV Import (existing) ═══ */}
      {(tab === 'enrolments' || tab === 'results') && (
      <>
      <div className="card mb-16">
        <div className="card-hdr">
          <span className="card-title">{tab === 'enrolments' ? 'Upload Enrolments' : 'Upload Results'}</span>
        </div>
        <div className="card-body">
          {tab === 'enrolments' ? (
            <>
              <p className="fs-13 mb-12" style={{ lineHeight: 1.7 }}>
                Upload a CSV to assign units to apprentices with year levels. This populates the Training Plan.
              </p>
              <div className="infobox mb-16">
                <strong>Required CSV columns:</strong><br />
                <code className="fs-12">apprentice_name, unit_code, year</code><br /><br />
                <strong>Example:</strong><br />
                <code className="fs-11 text-muted">
                  apprentice_name,unit_code,year<br />
                  Selene Boyd,AURASA102,1<br />
                  Selene Boyd,AURETR125,1<br />
                  Lily Harris,AURHTD102,2
                </code><br /><br />
                Apprentice can be matched by name, email, or student number. Column headers are flexible — just include words like "apprentice"/"name", "unit"/"code", and "year"/"level".
              </div>
            </>
          ) : (
            <>
              <p className="fs-13 mb-12" style={{ lineHeight: 1.7 }}>
                Upload a CSV to mark knowledge units as passed or exempt. This updates the knowledge completion status.
              </p>
              <div className="infobox mb-16">
                <strong>Required CSV columns:</strong><br />
                <code className="fs-12">apprentice_name, unit_code, status</code><br /><br />
                <strong>Status values:</strong> P/Passed/Pass = passed, E/Enrolled = skip (not yet passed), Ex/Exempt/RPL = exempt<br /><br />
                <strong>Example:</strong><br />
                <code className="fs-11 text-muted">
                  apprentice_name,unit_code,status<br />
                  Selene Boyd,AURASA102,P<br />
                  Selene Boyd,AURETR125,E<br />
                  Lily Harris,AURHTD102,Passed
                </code><br /><br />
                If no status column is provided, all rows default to "passed".
              </div>
            </>
          )}

          <div className="flex items-center gap-12">
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} className="fs-13" />
            {(preview || error) && (
              <button className="btn outline fs-12" onClick={reset}>Clear</button>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {preview && !result && (
        <div className="card mb-16">
          <div className="card-hdr">
            <span className="card-title">Preview — {preview.items.length} rows</span>
            <div className="flex gap-8">
              {validCount > 0 && <span className="pill pill-green">{validCount} ready</span>}
              {errorCount > 0 && <span className="pill pill-red">{errorCount} errors</span>}
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Apprentice</th>
                  <th>Unit Code</th>
                  {preview.type === 'enrolments' ? <th>Year</th> : <th>Status</th>}
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {preview.items.slice(0, 100).map((item, i) => (
                  <tr key={i} style={item.skip ? { opacity: 0.5 } : undefined}>
                    <td className="fs-11 text-muted">{item.row}</td>
                    <td>{item.apprenticeName}</td>
                    <td className="unit-code">{item.unitCode}</td>
                    {preview.type === 'enrolments' ? (
                      <td>Year {item.year}</td>
                    ) : (
                      <td>{item.statusRaw}</td>
                    )}
                    <td>
                      {item.valid && !item.alreadyPassed && <span className="fs-11 text-green">✓ Ready</span>}
                      {item.valid && item.alreadyPassed && <span className="fs-11 text-muted">Already done</span>}
                      {item.skip && <span className="fs-11 text-muted">Skip (enrolled)</span>}
                      {!item.valid && !item.skip && <span className="fs-11 text-red">{item.errors.join('; ')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.items.length > 100 && (
            <div className="card-footer fs-11 text-muted">Showing first 100 of {preview.items.length} rows</div>
          )}
          <div className="card-footer">
            <button className="btn" onClick={preview.type === 'enrolments' ? importEnrolments : importResults} disabled={importing || validCount === 0}>
              {importing ? 'Importing…' : `Import ${validCount} ${preview.type === 'enrolments' ? 'enrolments' : 'results'}`}
            </button>
            {validCount === 0 && <span className="fs-12 text-muted">No valid rows to import</span>}
          </div>
        </div>
      )}

      {result && (
        <div className="card">
          <div className="card-hdr">
            <span className="card-title">Import Complete</span>
          </div>
          <div className="card-body">
            <div className="flex flex-wrap gap-12 mb-16">
              <div className="result-stat success">
                <div className="result-stat-value">{result.success}</div>
                <div className="result-stat-label">Imported</div>
              </div>
              {result.failed > 0 && (
                <div className="result-stat error">
                  <div className="result-stat-value">{result.failed}</div>
                  <div className="result-stat-label">Failed</div>
                </div>
              )}
              {(result.skippedEnrolled || 0) > 0 && (
                <div className="result-stat neutral">
                  <div className="result-stat-value">{result.skippedEnrolled}</div>
                  <div className="result-stat-label">Enrolled (skipped)</div>
                </div>
              )}
              {(result.alreadyDone || 0) > 0 && (
                <div className="result-stat neutral">
                  <div className="result-stat-value">{result.alreadyDone}</div>
                  <div className="result-stat-label">Already done</div>
                </div>
              )}
            </div>
            <button className="btn outline" onClick={reset}>Upload another file</button>
          </div>
        </div>
      )}

      <div className="card mt-16">
        <div className="card-hdr"><span className="card-title">Download Templates</span></div>
        <div className="card-body flex gap-12 flex-wrap">
          <button className="btn outline" onClick={() => downloadTemplate('enrolments')}>📥 Enrolment Template</button>
          <button className="btn outline" onClick={() => downloadTemplate('results')}>📥 Results Template</button>
        </div>
      </div>
      </>
      )}

      {/* ═══ Trade School CSV ═══ */}
      {tab === 'tradeschool' && (
      <>
      <div className="card mb-16">
        <div className="card-hdr">
          <span className="card-title">🏫 Upload Trade School Hours</span>
        </div>
        <div className="card-body">
          <p className="fs-13 mb-12" style={{ lineHeight: 1.7 }}>
            Upload a CSV export from your HR platform to bulk-import trade school attendance hours.
          </p>
          <div className="infobox mb-16">
            <strong>Required CSV columns:</strong><br />
            <code className="fs-12">name, date, hours</code><br /><br />
            <strong>Optional:</strong> <code className="fs-12">topic</code> (defaults to "Trade school attendance")<br /><br />
            <strong>Date formats:</strong> DD/MM/YYYY or YYYY-MM-DD<br /><br />
            <strong>Example:</strong><br />
            <code className="fs-11 text-muted">
              apprentice_name,date,hours,topic<br />
              Selene Boyd,15/03/2025,8,Engine systems<br />
              Lily Harris,15/03/2025,8,Engine systems
            </code><br /><br />
            Apprentice can be matched by name, email, or student number. Duplicate entries (same apprentice, date, and hours) are automatically skipped.
          </div>

          <div className="flex items-center gap-12">
            <label className="btn outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
              📎 Choose CSV File
              <input ref={tsFileRef} type="file" accept=".csv,.txt" onChange={handleTsFile} style={{ display: 'none' }} />
            </label>
            {(tsPreview || tsResult || error) && (
              <button className="btn outline fs-12" onClick={resetTs}>Clear</button>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {tsPreview && !tsResult && (
        <div className="card mb-16">
          <div className="card-hdr">
            <span className="card-title">Preview — {tsPreview.items.length} rows, {tsTotalHours}h total</span>
            <div className="flex gap-8">
              {tsValidCount > 0 && <span className="pill pill-green">{tsValidCount} ready</span>}
              {tsDupeCount > 0 && <span className="pill" style={{ background: 'var(--panel2)', color: 'var(--muted)' }}>{tsDupeCount} duplicates</span>}
              {tsErrorCount > 0 && <span className="pill pill-red">{tsErrorCount} errors</span>}
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Apprentice</th>
                  <th>Date</th>
                  <th>Hours</th>
                  <th>Topic</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {tsPreview.items.slice(0, 100).map((item, i) => (
                  <tr key={i} style={item.isDuplicate ? { opacity: 0.5 } : undefined}>
                    <td className="fs-11 text-muted">{item.row}</td>
                    <td>{item.apprenticeName}</td>
                    <td style={{ fontSize: 12 }}>{item.date || item.dateRaw}</td>
                    <td style={{ fontWeight: 600 }}>{Math.round(item.hours * 100) / 100}h</td>
                    <td style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.topic}</td>
                    <td>
                      {item.valid && <span className="fs-11 text-green">✓ Ready</span>}
                      {item.isDuplicate && <span className="fs-11 text-muted">Duplicate — skip</span>}
                      {!item.valid && !item.isDuplicate && <span className="fs-11 text-red">{item.errors.join('; ')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {tsPreview.items.length > 100 && (
            <div className="card-footer fs-11 text-muted">Showing first 100 of {tsPreview.items.length} rows</div>
          )}
          <div className="card-footer">
            <button className="btn" onClick={importTradeSchool} disabled={tsImporting || tsValidCount === 0}>
              {tsImporting ? 'Importing…' : `Import ${tsValidCount} entries (${tsTotalHours}h)`}
            </button>
            {tsValidCount === 0 && <span className="fs-12 text-muted">No valid rows to import</span>}
          </div>
        </div>
      )}

      {tsResult && (
        <div className="card">
          <div className="card-hdr">
            <span className="card-title">Import Complete</span>
          </div>
          <div className="card-body">
            <div className="flex flex-wrap gap-12 mb-16">
              <div className="result-stat success">
                <div className="result-stat-value">{tsResult.success}</div>
                <div className="result-stat-label">Imported</div>
              </div>
              {tsResult.failed > 0 && (
                <div className="result-stat error">
                  <div className="result-stat-value">{tsResult.failed}</div>
                  <div className="result-stat-label">Failed</div>
                </div>
              )}
              {tsResult.duplicates > 0 && (
                <div className="result-stat neutral">
                  <div className="result-stat-value">{tsResult.duplicates}</div>
                  <div className="result-stat-label">Duplicates skipped</div>
                </div>
              )}
            </div>
            <button className="btn outline" onClick={resetTs}>Upload another file</button>
          </div>
        </div>
      )}

      <div className="card mt-16">
        <div className="card-hdr"><span className="card-title">Download Template</span></div>
        <div className="card-body">
          <button className="btn outline" onClick={() => downloadTemplate('tradeschool')}>📥 Trade School Template</button>
        </div>
      </div>
      </>
      )}

    </div>
  );
}

function downloadTemplate(type) {
  let csv;
  if (type === 'enrolments') {
    csv = 'apprentice_name,unit_code,year\nSelene Boyd,AURASA102,1\nSelene Boyd,AURETR125,1\nLily Harris,AURHTD102,2\n';
  } else if (type === 'tradeschool') {
    csv = 'apprentice_name,date,hours,topic\nSelene Boyd,15/03/2025,8,Engine systems\nLily Harris,15/03/2025,8,Engine systems\nSelene Boyd,22/03/2025,8,Braking systems\n';
  } else {
    csv = 'apprentice_name,unit_code,status\nSelene Boyd,AURASA102,P\nSelene Boyd,AURETR125,E\nLily Harris,AURHTD102,Passed\n';
  }
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${type}_template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
